import { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldAlert, ShieldCheck, RefreshCw, Loader2, AlertCircle, Mail } from 'lucide-react';
import {
  getDmarcReports,
  DmarcConfig,
  DmarcRollup,
  DmarcReportSummary,
} from '../../services/api';

interface Props {
  clientId: string;
  clientName: string;
}

function formatPercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function policyBadge(policy: string | null): { label: string; color: string; icon: typeof Shield } {
  if (!policy) return { label: 'No DMARC', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: ShieldAlert };
  if (policy === 'reject') return { label: 'p=reject', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: ShieldCheck };
  if (policy === 'quarantine') return { label: 'p=quarantine', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Shield };
  return { label: 'p=none (monitor)', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: Shield };
}

export default function ClientDmarc({ clientId, clientName }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<DmarcConfig | null>(null);
  const [rollups, setRollups] = useState<DmarcRollup[]>([]);
  const [reports, setReports] = useState<DmarcReportSummary[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDmarcReports(clientId, 30);
      setConfig(data.config);
      setRollups(data.rollups || []);
      setReports(data.reports || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load DMARC data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading DMARC data for {clientName}…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
        <AlertCircle className="h-5 w-5" />
        <div>{error}</div>
      </div>
    );
  }

  const totalMessages30d = rollups.reduce((sum, r) => sum + r.totalMessages, 0);
  const passing30d = rollups.reduce((sum, r) => sum + r.passingMessages, 0);
  const failing30d = rollups.reduce((sum, r) => sum + r.failingMessages, 0);
  const passRate30d = totalMessages30d ? passing30d / totalMessages30d : 0;

  const allIps = new Map<string, { ip: string; messages: number; passing: number; failing: number; headerFroms: Set<string> }>();
  for (const r of rollups) {
    for (const s of r.bySourceIp) {
      const existing = allIps.get(s.ip);
      if (existing) {
        existing.messages += s.messages;
        existing.passing += s.passing;
        existing.failing += s.failing;
        s.headerFroms.forEach((h) => existing.headerFroms.add(h));
      } else {
        allIps.set(s.ip, { ip: s.ip, messages: s.messages, passing: s.passing, failing: s.failing, headerFroms: new Set(s.headerFroms) });
      }
    }
  }
  const ipList = Array.from(allIps.values()).sort((a, b) => b.messages - a.messages);
  const topSenders = ipList.slice(0, 20);

  const policy = policyBadge(config?.currentPolicy || null);
  const PolicyIcon = policy.icon;

  return (
    <div className="space-y-6">
      {/* Policy + Status Card */}
      <div className="card p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="h-7 w-7 text-primary-500" />
            <div>
              <h2 className="text-xl font-semibold">DMARC Status</h2>
              <p className="text-sm text-muted-foreground">Policy domain: {config?.policyDomain || '—'}</p>
            </div>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Current policy</div>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium ${policy.color}`}>
              <PolicyIcon className="h-4 w-4" />
              {policy.label}
            </span>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Reports received</div>
            <div className="text-2xl font-semibold">{config?.totalReports ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Last report</div>
            <div className="text-sm">{config?.lastReportAt ? new Date(config.lastReportAt).toLocaleString() : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Onboarded</div>
            <div className="text-sm">{config?.onboardedAt ? new Date(config.onboardedAt).toLocaleDateString() : '—'}</div>
          </div>
        </div>
      </div>

      {/* 30-day summary */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Last 30 days</h3>
        {rollups.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No reports yet. Once a DMARC record with <code>rua=mailto:dmarc@umetech.net</code> is live, reports usually start arriving within 24h.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Stat label="Messages observed" value={totalMessages30d.toLocaleString()} />
            <Stat label="Passing alignment" value={passing30d.toLocaleString()} sub={formatPercent(passRate30d)} positive />
            <Stat label="Failing alignment" value={failing30d.toLocaleString()} negative={failing30d > 0} />
            <Stat label="Unique source IPs" value={ipList.length.toString()} />
          </div>
        )}
      </div>

      {/* Top senders */}
      {topSenders.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Top sending IPs (last 30 days)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4">Source IP</th>
                  <th className="py-2 pr-4 text-right">Messages</th>
                  <th className="py-2 pr-4 text-right">Pass</th>
                  <th className="py-2 pr-4 text-right">Fail</th>
                  <th className="py-2">Header From</th>
                </tr>
              </thead>
              <tbody>
                {topSenders.map((s) => {
                  const passPct = s.messages ? s.passing / s.messages : 0;
                  return (
                    <tr key={s.ip} className="border-b border-border/40">
                      <td className="py-2 pr-4 font-mono text-xs">{s.ip}</td>
                      <td className="py-2 pr-4 text-right">{s.messages.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right text-green-600 dark:text-green-400">
                        {s.passing.toLocaleString()} ({formatPercent(passPct)})
                      </td>
                      <td className={`py-2 pr-4 text-right ${s.failing > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                        {s.failing.toLocaleString()}
                      </td>
                      <td className="py-2 text-xs text-muted-foreground truncate max-w-xs">
                        {Array.from(s.headerFroms).slice(0, 3).join(', ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent reports */}
      {reports.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Recent reports
          </h3>
          <div className="space-y-2">
            {reports.slice(0, 10).map((r) => {
              const start = new Date(r.dateRangeBegin * 1000).toLocaleDateString();
              const end = new Date(r.dateRangeEnd * 1000).toLocaleDateString();
              return (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-md border border-border/40 text-sm">
                  <div>
                    <div className="font-medium">{r.reporter}</div>
                    <div className="text-xs text-muted-foreground">
                      {start} → {end} · {r.recordCount} record{r.recordCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{r.totalMessages.toLocaleString()} msgs</div>
                    <div className="text-xs text-muted-foreground">
                      <span className="text-green-600 dark:text-green-400">{r.passingMessages} pass</span>
                      {r.failingMessages > 0 && (
                        <> · <span className="text-red-600 dark:text-red-400">{r.failingMessages} fail</span></>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, positive, negative }: { label: string; value: string; sub?: string; positive?: boolean; negative?: boolean }) {
  const valueColor = positive ? 'text-green-600 dark:text-green-400' : negative ? 'text-red-600 dark:text-red-400' : '';
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`text-2xl font-semibold ${valueColor}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
